import apiResponse from '@stores/ApiResponse';
import { useQuery } from '@tanstack/react-query';
import {
  QuestionnaireAdminStore,
  QuestionnaireFormSummary,
  QuestionnaireFormVersionDetail,
  QuestionnaireFormVersionSummary,
  QuestionnaireResult,
  QuestionnaireResultDetail,
  QuestionnaireResultsCohort,
  QuestionnaireResultsFilter,
  QuestionnaireSchedule,
  QuestionnaireTimepoint,
  ScoredCodes,
} from '@stores/QuestionnaireAdminStore';
import { ViewCreatorStaleTime } from '@utils/queryCacheConfig';

/**
 * What the Questionnaires admin reads: the questionnaire list, a questionnaire's versions, one version's content, the
 * schedules and timepoints questionnaires are asked on, and participants' results.
 */
export class QuestionnaireAdminViewCreator {
  constructor(private readonly store: QuestionnaireAdminStore = new QuestionnaireAdminStore()) {}

  async readSummaries(): Promise<QuestionnaireFormSummary[]> {
    return await apiResponse.handleApiResponse(() => this.store.GetSummaries());
  }

  async readScoredCodes(): Promise<ScoredCodes> {
    return await apiResponse.handleApiResponse(() => this.store.GetScoredCodes());
  }

  async readVersions(formId: number): Promise<QuestionnaireFormVersionSummary[]> {
    return await apiResponse.handleApiResponse(() => this.store.GetVersions(formId));
  }

  async readVersion(formVersionId: number): Promise<QuestionnaireFormVersionDetail> {
    return await apiResponse.handleApiResponse(() => this.store.GetVersion(formVersionId));
  }

  async readSchedules(): Promise<QuestionnaireSchedule[]> {
    return await apiResponse.handleApiResponse(() => this.store.GetSchedules());
  }

  async readTimepoints(): Promise<QuestionnaireTimepoint[]> {
    return await apiResponse.handleApiResponse(() => this.store.GetTimepoints());
  }

  async readResults(filter: QuestionnaireResultsFilter): Promise<QuestionnaireResult[]> {
    return await apiResponse.handleApiResponse(() => this.store.GetResults(filter));
  }

  async readResult(sittingId: number): Promise<QuestionnaireResultDetail> {
    return await apiResponse.handleApiResponse(() => this.store.GetResult(sittingId));
  }

  async readCohorts(): Promise<QuestionnaireResultsCohort[]> {
    return await apiResponse.handleApiResponse(() => this.store.GetResultsCohorts());
  }
}

export const questionnaireAdminQueryKeys = {
  summaries: ['QuestionnaireAdminViewCreator', 'summaries'],
  scoredCodes: ['QuestionnaireAdminViewCreator', 'scoredCodes'],
  versions: (formId: number) => ['QuestionnaireAdminViewCreator', 'versions', formId],
  version: (formVersionId: number) => ['QuestionnaireAdminViewCreator', 'version', formVersionId],
  schedules: ['QuestionnaireAdminViewCreator', 'schedules'],
  timepoints: ['QuestionnaireAdminViewCreator', 'timepoints'],
  results: (filter: QuestionnaireResultsFilter) => ['QuestionnaireAdminViewCreator', 'results', filter],
  result: (sittingId: number) => ['QuestionnaireAdminViewCreator', 'result', sittingId],
  cohorts: ['QuestionnaireAdminViewCreator', 'cohorts'],
};

export const useQuestionnaireSummaries = () =>
  useQuery({
    queryKey: questionnaireAdminQueryKeys.summaries,
    queryFn: () => new QuestionnaireAdminViewCreator().readSummaries(),
    staleTime: ViewCreatorStaleTime.QuestionnaireAdminViewCreator,
  });

// Scorers only change with a deploy, so these never go stale. Loaded only once someone opens the list.
export const useScoredCodes = (enabled: boolean) =>
  useQuery({
    queryKey: questionnaireAdminQueryKeys.scoredCodes,
    queryFn: () => new QuestionnaireAdminViewCreator().readScoredCodes(),
    staleTime: Infinity,
    enabled: enabled,
  });

export const useQuestionnaireVersions = (formId: number) =>
  useQuery({
    queryKey: questionnaireAdminQueryKeys.versions(formId),
    queryFn: () => new QuestionnaireAdminViewCreator().readVersions(formId),
    staleTime: ViewCreatorStaleTime.QuestionnaireAdminViewCreator,
    enabled: formId > 0,
  });

// Published versions never change, so a loaded one never goes stale.
export const useQuestionnaireVersion = (formVersionId: number | null) =>
  useQuery({
    queryKey: questionnaireAdminQueryKeys.version(formVersionId ?? 0),
    queryFn: () => new QuestionnaireAdminViewCreator().readVersion(formVersionId!),
    staleTime: Infinity,
    enabled: !!formVersionId && formVersionId > 0,
  });

export const useQuestionnaireSchedules = () =>
  useQuery({
    queryKey: questionnaireAdminQueryKeys.schedules,
    queryFn: () => new QuestionnaireAdminViewCreator().readSchedules(),
    staleTime: ViewCreatorStaleTime.QuestionnaireAdminViewCreator,
  });

export const useQuestionnaireTimepoints = () =>
  useQuery({
    queryKey: questionnaireAdminQueryKeys.timepoints,
    queryFn: () => new QuestionnaireAdminViewCreator().readTimepoints(),
    staleTime: ViewCreatorStaleTime.QuestionnaireAdminViewCreator,
  });

export const useQuestionnaireResults = (filter: QuestionnaireResultsFilter, enabled: boolean) =>
  useQuery({
    queryKey: questionnaireAdminQueryKeys.results(filter),
    queryFn: () => new QuestionnaireAdminViewCreator().readResults(filter),
    staleTime: ViewCreatorStaleTime.QuestionnaireAdminViewCreator,
    enabled: enabled,
  });

export const useQuestionnaireResult = (sittingId: number | null) =>
  useQuery({
    queryKey: questionnaireAdminQueryKeys.result(sittingId ?? 0),
    queryFn: () => new QuestionnaireAdminViewCreator().readResult(sittingId!),
    staleTime: ViewCreatorStaleTime.QuestionnaireAdminViewCreator,
    enabled: !!sittingId && sittingId > 0,
  });

export const useQuestionnaireCohorts = () =>
  useQuery({
    queryKey: questionnaireAdminQueryKeys.cohorts,
    queryFn: () => new QuestionnaireAdminViewCreator().readCohorts(),
    staleTime: ViewCreatorStaleTime.QuestionnaireAdminViewCreator,
  });
